import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import building from '../../../../../public/assets/images/home-building.png';
import { Clock, Security } from 'iconsax-react';
import { ContactPerson } from '../../../components/freelancer/cards/ContactPersonCard';
import { PageHeader } from '../../../components/common/header/PageHeader';
import CustomSelect from '../../../components/common/inputs/CustomSelect';
import { black200, yellowMain, black500 } from '../../../constants/color';
import { InfoDetails } from '../myProfile/InfoDetail';
import { ProfileActionCard } from '../myProfile/ProfileAction';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import AssignmentApi from '@infrastructure/assignment-api';
import { assignmentErrorMessage } from '@application/features/assignments/assignment-error';
import { getCountriesAsync } from '@application/features/country/countryThunk';
import { useNavigate } from 'react-router-dom';
export const CurrentAssignment = () => {
    const [selectedClient, setSelectedClient] = React.useState('');
    const [Assignments, setAssignments] = useState([]);
    const [currentAssignment, setCurrentAssignment] = useState(null);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [listVersion, setListVersion] = useState(0);
    const listRequest = useRef(0);
    const detailRequest = useRef(0);
    const actorId = useAppSelector((state) => state.auth.currentUser?.id);
    const { countriesDropdown } = useAppSelector((state) => state.country);
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const handleContractTypeChange = (event) => {
        ++detailRequest.current;
        setCurrentAssignment(null);
        setDetailError(null);
        setSelectedClient(event.target.value);
    };
    const loadAssignments = useCallback(async () => {
        const request = ++listRequest.current;
        ++detailRequest.current;
        setListLoading(true);
        setListError(null);
        setAssignments([]);
        setSelectedClient('');
        setCurrentAssignment(null);
        setDetailError(null);
        try {
            const response = await AssignmentApi.getCurrentAssignments();
            if (request !== listRequest.current)
                return;
            if (!Array.isArray(response.data))
                throw new Error('Invalid assignment list response');
            setAssignments(response.data);
            setSelectedClient(response.data[0]?.id || '');
            // A refresh may return the same first ID in the same React batch.
            // Still reload its detail, including after a different actor signed in.
            setListVersion(request);
        }
        catch (error) {
            if (request === listRequest.current)
                setListError(assignmentErrorMessage(error, 'load your assignments'));
        }
        finally {
            if (request === listRequest.current)
                setListLoading(false);
        }
    }, [actorId]);
    const loadDetail = useCallback(async () => {
        const request = ++detailRequest.current;
        setCurrentAssignment(null);
        setDetailError(null);
        if (!selectedClient) {
            setDetailLoading(false);
            return;
        }
        setDetailLoading(true);
        try {
            const response = await AssignmentApi.getAssignmentDetailById(selectedClient);
            if (request !== detailRequest.current)
                return;
            if (!response.data)
                throw new Error('Invalid assignment detail response');
            setCurrentAssignment(response.data);
        }
        catch (error) {
            if (request === detailRequest.current)
                setDetailError(assignmentErrorMessage(error, 'load this assignment'));
        }
        finally {
            if (request === detailRequest.current)
                setDetailLoading(false);
        }
    }, [selectedClient, listVersion]);
    useEffect(() => {
        dispatch(getCountriesAsync());
        void loadAssignments();
        return () => { ++listRequest.current; ++detailRequest.current; };
    }, [dispatch, loadAssignments]);
    useEffect(() => {
        void loadDetail();
        return () => { ++detailRequest.current; };
    }, [loadDetail]);
    const isxs = useMediaQuery('(min-width:100px) and (max-width:750px)');
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: 'Current Assignment', children: _jsx(Box, { sx: {
                        display: 'flex',
                        gap: 1
                        // flexDirection: { sm: "column", md: "row" },
                    } }) }), _jsx(Box, { sx: { pl: { xs: 0, md: 7 } }, children: listLoading ? _jsx(Typography, { role: 'status', children: "Loading assignments\u2026" }) : listError ? (_jsx(Alert, { severity: 'error', action: _jsx(Button, { onClick: () => void loadAssignments(), children: "Retry assignments" }), children: listError })) : Assignments.length === 0 ? (_jsx(Alert, { severity: 'info', children: "No current assignment is available for your account." })) : (_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { xl: 4, lg: 3, item: true, md: 6, sm: 12, xs: 12, children: _jsxs(Box, { width: { xs: '100%', sm: '100%', md: '100%', lg: '100%', xl: 350 }, children: [_jsx(Typography, { variant: 'subtitle2', mb: 1, color: 'initial', children: "Your assignments" }), _jsx(CustomSelect, { label: 'Assignment', fillBackground: false, options: Assignments
                                            ? Assignments?.map((client) => ({
                                                label: client.clientName,
                                                value: client.id
                                            }))
                                            : [], defaultOption: 'No assignment', selectedOption: selectedClient, handleChange: handleContractTypeChange }), _jsx(Button, { onClick: () => void loadAssignments(), children: "Refresh assignments" })] }) }), detailLoading || !currentAssignment ? (_jsx(Grid, { item: true, lg: 9, md: 6, xs: 12, children: detailError ? _jsx(Alert, { severity: 'error', action: _jsx(Button, { onClick: () => void loadDetail(), children: "Retry assignment" }), children: detailError })
                                : _jsx(Typography, { role: 'status', children: "Loading assignment details\u2026" }) })) : (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, lg: 5, md: 6, xl: 5, xs: 12, children: _jsxs(Box, { sx: {
                                            minHeight: '490px',
                                            height: '100%',
                                            borderRadius: '25px',
                                            backgroundImage: `url(${building})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            alignItems: 'start',
                                            backgroundColor: '#222222',
                                            opacity: 15,
                                            padding: '40px',
                                            '&:hover': {
                                                opacity: 30
                                            }
                                        }, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: 'subtitle2', color: black200, children: "Current assignment" }), _jsx(Typography, { fontSize: '38px', fontWeight: 550, color: '#FFFFFF', children: currentAssignment?.name })] }), _jsxs(Box, { children: [_jsx(Typography, { fontSize: '20px', fontWeight: 500, color: yellowMain, children: "Client information" }), _jsxs(Box, { mt: 2, children: [_jsx(InfoDetails, { label: 'Client name', value: currentAssignment?.name, color: 'white', width: isxs ? 200 : 330 }), _jsx(InfoDetails, { color: 'white', label: 'Business address', value: currentAssignment?.address, width: isxs ? 200 : 330 }), _jsx(InfoDetails, { label: 'Postal code', color: 'white', value: currentAssignment?.postalCode, width: isxs ? 200 : 330 }), _jsx(InfoDetails, { label: 'KvK', color: 'white', value: currentAssignment?.kvk, width: isxs ? 200 : 330 }), _jsx(InfoDetails, { label: 'Country of establishment', width: isxs ? 200 : 330, color: 'white', value: countriesDropdown?.find((c) => c.value === currentAssignment?.countryId)?.label }), _jsx(InfoDetails, { label: 'BTW number', value: currentAssignment?.btw, color: 'white', width: isxs ? 200 : 330 }), _jsx(InfoDetails, { label: 'Bank account number', color: 'white', value: currentAssignment?.bankAccountNo, width: isxs ? 200 : 330 })] })] })] }) }), _jsx(Grid, { item: true, md: 12, sm: 12, lg: 4, xl: 3, xs: 12, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsxs(Grid, { item: true, md: 6, sm: 12, xs: 12, lg: 12, xl: 12, children: [_jsxs(Box, { sx: {
                                                            padding: '15px 25px',
                                                            background: 'white',
                                                            borderRadius: '25px',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }, children: [_jsxs(Box, { width: '50%', children: [_jsx(Clock, {}), _jsx(Typography, { variant: 'subtitle2', color: black500, children: "Monthly hours" }), _jsx(Typography, { fontSize: '32px', fontWeight: 550, color: black500, children: (() => {
                                                                            const selectedAssignment = Assignments?.find((x) => x.id === selectedClient);
                                                                            return selectedAssignment?.weeklyHours ? selectedAssignment.weeklyHours * 4 : 'N/A';
                                                                        })() })] }), _jsxs(Box, { width: '50%', children: [_jsx(Typography, { mt: 4, variant: 'subtitle2', color: black500, children: "Weekly hours" }), _jsx(Typography, { fontSize: '32px', fontWeight: 550, color: black500, children: (() => {
                                                                            const selectedAssignment = Assignments?.find((x) => x.id === selectedClient);
                                                                            return selectedAssignment?.weeklyHours || 'N/A';
                                                                        })() })] })] }), _jsx(ProfileActionCard, { subtitle: 'Fill in hours', mt: 2, bgColor: theme.palette.primary.main, leftIcon: _jsx(Security, {}), onClick: () => {
                                                            navigate('/hours-logging');
                                                        } })] }), _jsx(Grid, { item: true, md: 6, sm: 12, xs: 12, lg: 12, xl: 12, children: _jsx(ContactPerson, { client: currentAssignment }) })] }) })] }))] })) })] }));
};
{
    /* <Box
sx={{
  width: { xs: "150px", lg: "190px" },
  height: { xs: "150px", lg: "190px" },
  borderRadius: "50%",
  background: yellowMain,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  bottom: 0,
  position: "absolute",
  right: 0,
}}
>
<Typography variant="subtitle2" color={black500}>
  Fill in hours
</Typography>
<IconButton
  sx={{ border: "1px solid #D7D7D7", mt: 2 }}
  // onClick={() => navigate(`${to}`)}
>
  <ArrowDown
    style={{ color: "#D7D7D7", transform: "rotate(-45deg)" }}
  />
</IconButton>
</Box> */
}
